// @section 1 On-device card OCR (Tesseract.js, WASM).
//
// Runs entirely in the phone's browser: no server, no API, no per-call billing.
// The engine + English model load once (from the tesseract.js CDN) and cache in
// the browser, so repeat scans are fast and work with flaky connectivity after
// the first successful load. This is the PRIMARY OCR path; the Azure Vision Read
// backup (Phase 2) only runs server-side when this comes back weak.
//
// Business cards are a hard OCR target (logos, script fonts, reverse text), so
// treat the output as a pre-fill for the review form, not ground truth.

import { createWorker, type Worker } from 'tesseract.js';

let workerPromise: Promise<Worker> | null = null;

// @block 1.1 Lazily create ONE shared worker (spinning one up per scan is slow).
function getWorker(): Promise<Worker> {
  if (!workerPromise) {
    workerPromise = createWorker('eng').catch((e) => {
      // Reset so a later scan can retry after a transient load failure.
      workerPromise = null;
      throw e;
    });
  }
  return workerPromise;
}

// @block 1.2 Pre-warm: kick off the engine + model download ahead of the first
// scan so recognition feels instant in front of a prospect (the demo IS the sell).
// Fire-and-forget and safe to call repeatedly; a real scan surfaces any load error.
export function warmUpCardOcr(): void {
  void getWorker().catch(() => { /* deferred to the first recognizeCard call */ });
}

export type CardOcrResult = { text: string; confidence: number };

// @block 1.3 Recognize a card image. Never throws - OCR is best-effort and the
// form stays usable by hand; on failure returns empty text so the caller falls
// back to manual entry (or the cloud backup).
export async function recognizeCard(image: Blob | File): Promise<CardOcrResult> {
  try {
    const worker = await getWorker();
    const { data } = await worker.recognize(image);
    return { text: data.text ?? '', confidence: data.confidence ?? 0 };
  } catch {
    return { text: '', confidence: 0 };
  }
}

// @block 1.4 Free the worker (and its WASM heap) when the capture surface unmounts.
export async function disposeCardOcr(): Promise<void> {
  if (!workerPromise) return;
  const p = workerPromise;
  workerPromise = null;
  try {
    const worker = await p;
    await worker.terminate();
  } catch {
    /* already gone */
  }
}
